import React from "react";
import { Calendar, Database, ToggleLeft } from "react-feather";
import { FolderTree } from "lucide-react";
import StatusPill from "../../atoms/StatusPill/StatusPill";
import { KgInspectorDetailRow } from "../KgInspectorDetailRow";
import { KgInspectorExpandableSection } from "../KgInspectorExpandableSection";
import { RowStack } from "./KgInspectorDataSourceOverview.styles";
import type { KgInspectorDataSourceOverviewProps } from "./KgInspectorDataSourceOverview.types";
import type { KgInspectorOverviewFieldView } from "../../kg/canvas/inspector/types";

const STATUS_VARIANTS: Record<
  string,
  "success" | "neutral" | "error" | "warning"
> = {
  Connected: "success",
  Disconnected: "neutral",
  Error: "error",
  Pending: "warning",
  Unknown: "neutral",
};

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fieldIcon = (field: KgInspectorOverviewFieldView) => {
  switch (field.key) {
    case "type":
      return <Database size={16} />;
    case "status":
      return <ToggleLeft size={16} />;
    case "connectedOn":
      return <Calendar size={16} />;
    case "instances":
      return <FolderTree size={16} strokeWidth={1.75} />;
    default:
      return <Database size={16} />;
  }
};

const renderFieldValue = (field: KgInspectorOverviewFieldView) => {
  switch (field.valueType) {
    case "status": {
      const label = String(field.value);
      const variant = STATUS_VARIANTS[label] ?? "neutral";
      return (
        <StatusPill
          variant={variant}
          appearance={label === "Connected" ? "outline" : "filled"}
          showDot={false}
        >
          {label}
        </StatusPill>
      );
    }
    case "date":
      return formatDate(String(field.value));
    case "tags":
      return null;
    default:
      return String(field.value);
  }
};

export const KgInspectorDataSourceOverview: React.FC<
  KgInspectorDataSourceOverviewProps
> = ({ overview, isInstancesOpen = false, onToggleInstances }) => {
  const fields = overview?.fields ?? [];
  const tagsField = fields.find((field) => field.valueType === "tags");
  const instanceNames = Array.isArray(tagsField?.value) ? tagsField.value : [];
  const detailFields = fields.filter((field) => field.valueType !== "tags");

  return (
    <RowStack>
      {detailFields.map((field) => (
        <KgInspectorDetailRow
          key={field.key}
          icon={fieldIcon(field)}
          label={field.label}
          value={renderFieldValue(field)}
        />
      ))}
      {tagsField && instanceNames.length > 0 && (
        <KgInspectorExpandableSection
          variant="pills"
          icon={fieldIcon(tagsField)}
          label={tagsField.label}
          items={instanceNames}
          isOpen={isInstancesOpen}
          onToggle={onToggleInstances}
        />
      )}
    </RowStack>
  );
};

export default KgInspectorDataSourceOverview;
